import { REPORT_TEMPLATE_SCHEMA_VERSION } from './reportTemplateSchema';
import {
  STORAGE_PREFIXES,
  getCandidateKeysFromCurrentKey,
  getPrimaryStorageKey,
} from './storageKeys';

const DRAFT_STORAGE_PREFIX = getPrimaryStorageKey(STORAGE_PREFIXES.reportDraft);
const DRAFT_LOCAL_SCHEMA_VERSION = 2;
const DRAFT_STORAGE_DB_NAME = 'AgriSenseDraftStorage';
const DRAFT_STORAGE_DB_VERSION = 1;
const DRAFT_STORAGE_STORE_NAME = 'reportDrafts';
const STORAGE_ERROR_CODES = new Set([
  'quota_exceeded',
  'storage_unavailable',
  'storage_write_failed',
]);

const DEFAULT_DRAFT_SYNC_STATE = Object.freeze({
  status: 'local_only',
  serverRevision: null,
  lastSyncedAt: null,
  lastErrorCode: null,
  lastErrorMessage: null,
  retryAt: null,
});

let draftDbPromise = null;

const createStorageError = (code, message, cause) => {
  const error = new Error(message);
  error.code = code;
  if (cause) {
    error.cause = cause;
  }
  return error;
};

const isQuotaExceededError = (error) => {
  if (!error) {
    return false;
  }

  const name = typeof error.name === 'string' ? error.name : '';
  const message = typeof error.message === 'string' ? error.message : '';
  const numericCode = Number(error.code);

  return (
    name === 'QuotaExceededError'
    || name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || numericCode === 22
    || numericCode === 1014
    || /quota/i.test(message)
  );
};

const normalizeStorageError = (error, options = {}) => {
  if (error && typeof error.code === 'string' && STORAGE_ERROR_CODES.has(error.code)) {
    return error;
  }

  const fallbackCode = options.fallbackCode || 'storage_write_failed';
  const fallbackMessage = options.fallbackMessage || 'Storage operation failed.';

  if (isQuotaExceededError(error)) {
    return createStorageError('quota_exceeded', 'Browser storage quota exceeded.', error);
  }

  return createStorageError(fallbackCode, fallbackMessage, error);
};

const getDraftStorageKey = (reportId, locale) => {
  if (!reportId || !locale) {
    return null;
  }
  return getPrimaryStorageKey(STORAGE_PREFIXES.reportDraft, `${reportId}:${locale}`);
};

const getDraftStorageKeyCandidates = (storageKey) => getCandidateKeysFromCurrentKey(
  storageKey,
  STORAGE_PREFIXES.reportDraft
);

const normalizeDraftSyncState = (value) => {
  const candidate = value && typeof value === 'object' ? value : {};
  const normalizedStatus = typeof candidate.status === 'string' && candidate.status.trim()
    ? candidate.status.trim()
    : DEFAULT_DRAFT_SYNC_STATE.status;
  const normalizedServerRevision = Number.isFinite(Number(candidate.serverRevision))
    ? Number(candidate.serverRevision)
    : null;
  const normalizeIsoOrNull = (input) => (
    typeof input === 'string' && input.trim() && !Number.isNaN(new Date(input).getTime())
      ? input
      : null
  );
  return {
    status: normalizedStatus,
    serverRevision: normalizedServerRevision,
    lastSyncedAt: normalizeIsoOrNull(candidate.lastSyncedAt),
    lastErrorCode:
      typeof candidate.lastErrorCode === 'string' && candidate.lastErrorCode.trim()
        ? candidate.lastErrorCode.trim()
        : null,
    lastErrorMessage:
      typeof candidate.lastErrorMessage === 'string' && candidate.lastErrorMessage.trim()
        ? candidate.lastErrorMessage
        : null,
    retryAt: normalizeIsoOrNull(candidate.retryAt),
  };
};

const normalizeStoredDraftPayload = (draft) => {
  if (!draft || typeof draft !== 'object') {
    return null;
  }

  const serializedEditorState = getSerializedEditorState(draft);
  if (!serializedEditorState) {
    return null;
  }

  const normalized = {
    ...draft,
    schemaVersion: Number.isFinite(Number(draft.schemaVersion))
      ? Number(draft.schemaVersion)
      : DRAFT_LOCAL_SCHEMA_VERSION,
    reportId: typeof draft.reportId === 'string' ? draft.reportId : null,
    locale: typeof draft.locale === 'string' ? draft.locale : null,
    templateVersion: getDraftTemplateVersion(draft),
    timestamp:
      typeof draft.timestamp === 'string' && !Number.isNaN(new Date(draft.timestamp).getTime())
        ? draft.timestamp
        : new Date().toISOString(),
    editorState: serializedEditorState,
    clientInstanceId:
      typeof draft.clientInstanceId === 'string' && draft.clientInstanceId.trim()
        ? draft.clientInstanceId.trim()
        : null,
    clientRevision: Number.isFinite(Number(draft.clientRevision))
      ? Number(draft.clientRevision)
      : 0,
    sync: normalizeDraftSyncState(draft.sync),
  };

  return normalized;
};

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
};

const getIndexedDb = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.indexedDB || null;
  } catch (error) {
    return null;
  }
};

const readLegacyDraftEntryFromLocalStorage = (storageKey) => {
  if (!storageKey) {
    return null;
  }
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }
  const storageKeys = getDraftStorageKeyCandidates(storageKey);
  for (const key of storageKeys) {
    try {
      const rawValue = storage.getItem(key);
      if (!rawValue) {
        continue;
      }
      return {
        key,
        draft: JSON.parse(rawValue),
      };
    } catch (error) {
      return null;
    }
  }
  return null;
};

const loadLegacyDraftFromLocalStorage = (storageKey) => readLegacyDraftEntryFromLocalStorage(storageKey)?.draft || null;

const loadDraftFromStorage = (storageKey) => loadLegacyDraftFromLocalStorage(storageKey);

const saveDraftToLocalStorage = (storageKey, payload) => {
  const storage = getLocalStorage();
  if (!storage) {
    throw createStorageError(
      'storage_unavailable',
      'Local storage is not available.'
    );
  }
  try {
    storage.setItem(storageKey, JSON.stringify(payload));
  } catch (error) {
    throw normalizeStorageError(error, {
      fallbackCode: 'storage_write_failed',
      fallbackMessage: 'Failed to write draft to local storage.',
    });
  }
};

const removeLegacyDraftFromLocalStorage = (storageKey) => {
  if (!storageKey) {
    return;
  }
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    getDraftStorageKeyCandidates(storageKey).forEach((candidateKey) => {
      storage.removeItem(candidateKey);
    });
  } catch (error) {
    // Ignore cleanup failures.
  }
};

const requestToPromise = (request, fallbackMessage) => new Promise((resolve, reject) => {
  request.onsuccess = () => {
    resolve(request.result);
  };
  request.onerror = () => {
    reject(normalizeStorageError(request.error, {
      fallbackCode: 'storage_write_failed',
      fallbackMessage,
    }));
  };
});

const transactionToPromise = (transaction, fallbackMessage) => new Promise((resolve, reject) => {
  let settled = false;
  const finishResolve = () => {
    if (!settled) {
      settled = true;
      resolve();
    }
  };
  const finishReject = () => {
    if (!settled) {
      settled = true;
      reject(normalizeStorageError(transaction.error, {
        fallbackCode: 'storage_write_failed',
        fallbackMessage,
      }));
    }
  };

  transaction.oncomplete = finishResolve;
  transaction.onabort = finishReject;
  transaction.onerror = finishReject;
});

const openDb = async () => {
  const indexedDb = getIndexedDb();
  if (!indexedDb) {
    throw createStorageError(
      'storage_unavailable',
      'IndexedDB is not available in this browser.'
    );
  }

  if (!draftDbPromise) {
    draftDbPromise = new Promise((resolve, reject) => {
      let request;
      try {
        request = indexedDb.open(DRAFT_STORAGE_DB_NAME, DRAFT_STORAGE_DB_VERSION);
      } catch (error) {
        reject(normalizeStorageError(error, {
          fallbackCode: 'storage_write_failed',
          fallbackMessage: 'Failed to open IndexedDB for report drafts.',
        }));
        return;
      }

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(DRAFT_STORAGE_STORE_NAME)) {
          db.createObjectStore(DRAFT_STORAGE_STORE_NAME);
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(normalizeStorageError(request.error, {
          fallbackCode: 'storage_write_failed',
          fallbackMessage: 'Failed to open IndexedDB for report drafts.',
        }));
      };

      request.onblocked = () => {
        reject(createStorageError(
          'storage_write_failed',
          'Draft storage is blocked by another browser tab.'
        ));
      };
    });

    draftDbPromise.catch(() => {
      draftDbPromise = null;
    });
  }

  return draftDbPromise;
};

const idbGet = async (storageKey) => {
  if (!storageKey) {
    return null;
  }
  const db = await openDb();
  let transaction;
  try {
    transaction = db.transaction(DRAFT_STORAGE_STORE_NAME, 'readonly');
    const transactionDone = transactionToPromise(
      transaction,
      'Failed to read draft from IndexedDB.'
    );
    const store = transaction.objectStore(DRAFT_STORAGE_STORE_NAME);
    const resultPromise = requestToPromise(
      store.get(storageKey),
      'Failed to read draft from IndexedDB.'
    );
    const value = await resultPromise;
    await transactionDone;
    return value || null;
  } catch (error) {
    throw normalizeStorageError(error, {
      fallbackCode: 'storage_write_failed',
      fallbackMessage: 'Failed to read draft from IndexedDB.',
    });
  }
};

const idbPut = async (storageKey, payload) => {
  const db = await openDb();
  try {
    const transaction = db.transaction(DRAFT_STORAGE_STORE_NAME, 'readwrite');
    const transactionDone = transactionToPromise(
      transaction,
      'Failed to write draft to IndexedDB.'
    );
    const store = transaction.objectStore(DRAFT_STORAGE_STORE_NAME);
    store.put(payload, storageKey);
    await transactionDone;
  } catch (error) {
    throw normalizeStorageError(error, {
      fallbackCode: 'storage_write_failed',
      fallbackMessage: 'Failed to write draft to IndexedDB.',
    });
  }
};

const idbDelete = async (storageKey) => {
  if (!storageKey) {
    return;
  }
  const db = await openDb();
  try {
    const transaction = db.transaction(DRAFT_STORAGE_STORE_NAME, 'readwrite');
    const transactionDone = transactionToPromise(
      transaction,
      'Failed to delete draft from IndexedDB.'
    );
    const store = transaction.objectStore(DRAFT_STORAGE_STORE_NAME);
    store.delete(storageKey);
    await transactionDone;
  } catch (error) {
    // Ignore cleanup failures.
  }
};

const loadReportDraft = async (storageKey) => {
  if (!storageKey) {
    return null;
  }

  const storageKeys = getDraftStorageKeyCandidates(storageKey);
  const [primaryStorageKey, ...legacyStorageKeys] = storageKeys;
  let canMigrateLegacyDraft = false;

  try {
    const indexedDbDraft = await idbGet(primaryStorageKey);
    canMigrateLegacyDraft = true;
    if (indexedDbDraft && typeof indexedDbDraft === 'object') {
      return indexedDbDraft;
    }
    for (const legacyKey of legacyStorageKeys) {
      const legacyIndexedDbDraft = await idbGet(legacyKey);
      if (legacyIndexedDbDraft && typeof legacyIndexedDbDraft === 'object') {
        try {
          await idbPut(primaryStorageKey, legacyIndexedDbDraft);
          await idbDelete(legacyKey);
        } catch (error) {
          // Keep the legacy record when migration fails.
        }
        return legacyIndexedDbDraft;
      }
    }
  } catch (error) {
    canMigrateLegacyDraft = false;
  }

  const legacyDraftEntry = readLegacyDraftEntryFromLocalStorage(storageKey);
  if (!legacyDraftEntry?.draft) {
    return null;
  }

  if (canMigrateLegacyDraft) {
    try {
      await idbPut(primaryStorageKey, legacyDraftEntry.draft);
      removeLegacyDraftFromLocalStorage(storageKey);
    } catch (error) {
      // Keep the legacy copy when migration fails.
    }
  }

  return legacyDraftEntry.draft;
};

const saveReportDraft = async (storageKey, payload) => {
  if (!storageKey || typeof window === 'undefined') {
    throw createStorageError(
      'storage_unavailable',
      'Draft storage is not available in this environment.'
    );
  }

  try {
    await idbPut(storageKey, payload);
    const [, ...legacyStorageKeys] = getDraftStorageKeyCandidates(storageKey);
    await Promise.all(legacyStorageKeys.map((legacyKey) => idbDelete(legacyKey)));
    removeLegacyDraftFromLocalStorage(storageKey);
    return { backend: 'indexeddb' };
  } catch {
    try {
      saveDraftToLocalStorage(storageKey, payload);
      return { backend: 'localstorage' };
    } catch (localError) {
      throw normalizeStorageError(localError, {
        fallbackCode: 'storage_write_failed',
        fallbackMessage: 'Failed to persist report draft.',
      });
    }
  }
};

const removeReportDraft = async (storageKey) => {
  if (!storageKey) {
    return;
  }
  const storageKeys = getDraftStorageKeyCandidates(storageKey);
  try {
    await Promise.all(storageKeys.map((candidateKey) => idbDelete(candidateKey)));
  } catch (error) {
    // Ignore cleanup failures.
  }
  removeLegacyDraftFromLocalStorage(storageKey);
};

const getSerializedEditorState = (draft) => {
  if (!draft || !draft.editorState) {
    return null;
  }
  if (typeof draft.editorState === 'string') {
    try {
      JSON.parse(draft.editorState);
      return draft.editorState;
    } catch (error) {
      return null;
    }
  }
  try {
    return JSON.stringify(draft.editorState);
  } catch (error) {
    return null;
  }
};

const getDraftTemplateVersion = (draft) => {
  if (!draft || typeof draft !== 'object') {
    return null;
  }
  if (typeof draft.templateVersion === 'string' && draft.templateVersion.trim()) {
    return draft.templateVersion.trim();
  }
  return null;
};

const isLegacyDraftPayload = (draft) => {
  const version = getDraftTemplateVersion(draft);
  return !version || version !== REPORT_TEMPLATE_SCHEMA_VERSION;
};

export {
  DRAFT_LOCAL_SCHEMA_VERSION,
  DRAFT_STORAGE_PREFIX,
  DEFAULT_DRAFT_SYNC_STATE,
  getDraftTemplateVersion,
  getDraftStorageKey,
  loadDraftFromStorage,
  loadLegacyDraftFromLocalStorage,
  loadReportDraft,
  saveReportDraft,
  removeReportDraft,
  isLegacyDraftPayload,
  getSerializedEditorState,
  normalizeDraftSyncState,
  normalizeStoredDraftPayload,
};
